/**
 * FastAPI Session/Audit Client
 *
 * Persists session state and audit logs against the PostgreSQL-backed FastAPI
 * endpoints (`/api/sessions/*`, `/api/audit`).
 *
 * The client boundary translates between the backend wire format and two frontend
 * conventions that every consumer (hooks, pages, contexts) relies on:
 *   1. `status`/`action` are exposed as the integer codes (`SessionStatus`/
 *      `AuditAction`), even though the backend speaks strings.
 *   2. `details` is exposed as a JSON **string** (consumers `JSON.parse` it), even
 *      though the backend stores/returns it as a JSON object (JSONB).
 */

import { BRRSession, BRRAuditLog } from './types';
import { SessionStatus, AuditAction } from './sessionStatus';
import { authFetch } from './authFetch';

interface SessionApiConfig {
  baseUrl: string;
}

const STATUS_INT_TO_STRING: Record<number, string> = {
  [SessionStatus.Draft]: 'draft',
  [SessionStatus.InProgress]: 'in_progress',
  [SessionStatus.Submitted]: 'submitted',
  [SessionStatus.Completed]: 'completed',
  [SessionStatus.Rejected]: 'rejected',
};

const STATUS_STRING_TO_INT: Record<string, number> = Object.fromEntries(
  Object.entries(STATUS_INT_TO_STRING).map(([int, str]) => [str, Number(int)])
);

const ACTION_INT_TO_STRING: Record<number, string> = {
  [AuditAction.Started]: 'started',
  [AuditAction.Saved]: 'saved',
  [AuditAction.Submitted]: 'submitted',
  [AuditAction.Approved]: 'approved',
  [AuditAction.Rejected]: 'rejected',
  [AuditAction.Reassigned]: 'reassigned',
};

const ACTION_STRING_TO_INT: Record<string, number> = Object.fromEntries(
  Object.entries(ACTION_INT_TO_STRING).map(([int, str]) => [str, Number(int)])
);

/**
 * Backend JSON keys, keyed by the logical column names consumers use. Consumers
 * read record fields via this map, so returning the raw snake_case backend object
 * already satisfies every `record[columns.x]` access.
 * `name` has no backend equivalent; it is kept only for interface compatibility
 * and is not read by any consumer.
 */
const columns = {
  sessionid: 'id',
  name: 'name',
  batchid: 'batch_id',
  useremail: 'user_email',
  status: 'status',
  startedat: 'started_at',
  lastsavedat: 'last_saved_at',
  currentpage: 'current_page',
  assignedqp: 'assigned_qp',
  submittedat: 'submitted_at',
  completedat: 'completed_at',
  logid: 'id',
  action: 'action',
  timestamp: 'timestamp',
  details: 'details',
  ipaddress: 'ip_address',
  productname: 'product_name',
} as const;

/**
 * Serialize the backend `details` object into the JSON string consumers expect.
 * Consumers universally `JSON.parse(record[columns.details])`, so an already-string
 * value is passed through and a null/undefined value stays undefined.
 */
const detailsToString = (details: unknown): string | undefined => {
  if (details === null || details === undefined) return undefined;
  if (typeof details === 'string') return details;
  return JSON.stringify(details);
};

/**
 * Parse the JSON string that consumers pass back into the object the backend
 * (JSONB) expects. A non-JSON string is forwarded as-is (JSONB accepts scalars).
 */
const detailsToObject = (details?: string): unknown => {
  if (details === undefined || details === null || details === '') return undefined;
  try {
    return JSON.parse(details);
  } catch {
    return details;
  }
};

/**
 * Turn a backend `SessionResponse` into the shape consumers expect:
 * integer status code and stringified details, all other snake_case keys untouched.
 */
const mapSession = (raw: Record<string, unknown>): BRRSession => ({
  ...raw,
  status: STATUS_STRING_TO_INT[raw.status as string],
  details: detailsToString(raw.details),
});

/**
 * Turn a backend `AuditLogResponse` into the shape consumers expect:
 * integer action code and stringified details.
 */
const mapAuditLog = (raw: Record<string, unknown>): BRRAuditLog => ({
  ...raw,
  action: ACTION_STRING_TO_INT[raw.action as string],
  details: detailsToString(raw.details),
});

const toIso = (value: Date | string): string =>
  value instanceof Date ? value.toISOString() : value;

/**
 * Creates a FastAPI session/audit client that satisfies the `SessionAuditClient`
 * interface. Base URL matches `createBRRApi` (e.g. `http://localhost:8000`).
 */
export const createSessionApi = (config: SessionApiConfig) => {
  const { baseUrl } = config;

  const jsonHeaders = { 'Content-Type': 'application/json' };

  const getJson = async <T>(path: string): Promise<T> => {
    const response = await authFetch(`${baseUrl}${path}`, { method: 'GET' });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Session API error (${response.status}): ${text || response.statusText}`);
    }
    return response.json();
  };

  const client = {
    columns,

    async getDraftSessions(): Promise<BRRSession[]> {
      const rows = await getJson<Record<string, unknown>[]>('/api/sessions/draft');
      return rows.map(mapSession);
    },

    async getInProgressSessions(): Promise<BRRSession[]> {
      return this.getDraftSessions();
    },

    async getSession(sessionId: string): Promise<BRRSession | null> {
      try {
        const response = await authFetch(`${baseUrl}/api/sessions/${sessionId}`, { method: 'GET' });
        if (response.status === 404) {
          return null;
        }
        if (!response.ok) {
          throw new Error(`Session API error (${response.status})`);
        }
        return mapSession(await response.json());
      } catch (error) {
        console.error(`Failed to get session ${sessionId}:`, error);
        return null;
      }
    },

    async createSession(data: {
      batchId: string;
      currentPage?: string;
      productName?: string;
    }): Promise<{ id: string }> {
      const body: Record<string, unknown> = {
        batch_id: data.batchId,
        current_page: data.currentPage || 'form',
      };
      if (data.productName) {
        body.product_name = data.productName;
      }

      const response = await authFetch(`${baseUrl}/api/sessions`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Failed to create BRR session (${response.status}): ${text || response.statusText}`);
      }
      const session = await response.json();
      return { id: session.id };
    },

    async updateSession(
      sessionId: string,
      updates: {
        batchId?: string;
        lastSavedAt?: Date;
        currentPage?: string;
        status?: number;
        assignedQP?: string;
        submittedAt?: Date;
        completedAt?: Date;
        details?: string;
        /**
         * Assigns the session's owner (reassignment). Unlike everywhere else, this
         * stays client-supplied: it names another user, not the caller (DIMAPP-99).
         */
        userEmail?: string;
      }
    ): Promise<void> {
      const body: Record<string, unknown> = {};
      if (updates.batchId !== undefined) body.batch_id = updates.batchId;
      if (updates.lastSavedAt) body.last_saved_at = toIso(updates.lastSavedAt);
      if (updates.currentPage !== undefined) body.current_page = updates.currentPage;
      if (updates.status !== undefined) body.status = STATUS_INT_TO_STRING[updates.status];
      if (updates.assignedQP !== undefined) body.assigned_qp = updates.assignedQP;
      if (updates.submittedAt) body.submitted_at = toIso(updates.submittedAt);
      if (updates.completedAt) body.completed_at = toIso(updates.completedAt);
      if (updates.details !== undefined) body.details = detailsToObject(updates.details);
      if (updates.userEmail !== undefined) body.user_email = updates.userEmail;

      const response = await authFetch(`${baseUrl}/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Failed to update session (${response.status}): ${text || response.statusText}`);
      }
    },

    async deleteSession(sessionId: string): Promise<void> {
      const response = await authFetch(`${baseUrl}/api/sessions/${sessionId}`, { method: 'DELETE' });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Failed to delete session (${response.status}): ${text || response.statusText}`);
      }
    },

    async createAuditLog(data: {
      batchId: string;
      sessionId: string;
      action: number;
      details?: string;
      ipAddress?: string;
    }): Promise<{ id: string }> {
      try {
        const body: Record<string, unknown> = {
          batch_id: data.batchId,
          action: ACTION_INT_TO_STRING[data.action],
        };
        const details = detailsToObject(data.details);
        if (details !== undefined) body.details = details;
        if (data.ipAddress) body.ip_address = data.ipAddress;

        const response = await authFetch(`${baseUrl}/api/sessions/${data.sessionId}/audit`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          throw new Error(`Session API error (${response.status})`);
        }
        const log = await response.json();
        return { id: log.id };
      } catch (error) {
        // Audit logging failures must not block user operations.
        console.error('[SessionApi] Failed to create audit log:', error);
        return { id: '' };
      }
    },

    async getAuditLog(batchId: string): Promise<BRRAuditLog[]> {
      try {
        const rows = await getJson<Record<string, unknown>[]>(
          `/api/audit?batch_id=${encodeURIComponent(batchId)}`
        );
        return rows.map(mapAuditLog);
      } catch (error) {
        console.error(`Failed to get audit log for batch ${batchId}:`, error);
        return [];
      }
    },

    async logSave(
      batchId: string,
      sessionId: string,
      page?: string,
      fieldsChanged?: string[]
    ): Promise<void> {
      const detailsObj: { page?: string; fieldsChanged?: string[] } = {};
      if (page) detailsObj.page = page;
      if (fieldsChanged && fieldsChanged.length > 0) detailsObj.fieldsChanged = fieldsChanged;

      await this.createAuditLog({
        batchId,
        sessionId,
        action: AuditAction.Saved,
        details: Object.keys(detailsObj).length > 0 ? JSON.stringify(detailsObj) : undefined,
      });

      await this.updateSession(sessionId, { lastSavedAt: new Date() });
    },

    async logSubmission(
      batchId: string,
      sessionId: string,
      assignedQP: string
    ): Promise<void> {
      await this.createAuditLog({
        batchId,
        sessionId,
        action: AuditAction.Submitted,
        details: JSON.stringify({ assignedQP }),
      });

      await this.updateSession(sessionId, {
        status: SessionStatus.Submitted,
        assignedQP,
        submittedAt: new Date(),
      });
    },

    async logApproval(
      batchId: string,
      sessionId: string,
      comments?: string
    ): Promise<void> {
      await this.createAuditLog({
        batchId,
        sessionId,
        action: AuditAction.Approved,
        details: comments ? JSON.stringify({ comments }) : undefined,
      });

      await this.updateSession(sessionId, {
        status: SessionStatus.Completed,
        completedAt: new Date(),
      });
    },

    async getRecentSessions(top = 5): Promise<BRRSession[]> {
      try {
        const rows = await getJson<Record<string, unknown>[]>(
          `/api/sessions/recent?top=${top}`
        );
        return rows.map(mapSession);
      } catch (error) {
        console.error('Failed to get recent sessions:', error);
        return [];
      }
    },

    async getAllSessions(top = 500): Promise<{ sessions: BRRSession[]; isTruncated: boolean }> {
      const result = await getJson<{ sessions: Record<string, unknown>[]; is_truncated: boolean }>(
        `/api/sessions/all?top=${top}`
      );
      return {
        sessions: result.sessions.map(mapSession),
        isTruncated: result.is_truncated,
      };
    },

    async getSessionsByStatus(status: number): Promise<BRRSession[]> {
      const rows = await getJson<Record<string, unknown>[]>(
        `/api/sessions/by-status?status=${encodeURIComponent(STATUS_INT_TO_STRING[status])}`
      );
      return rows.map(mapSession);
    },

    async getCompletedSessions(): Promise<BRRSession[]> {
      return this.getSessionsByStatus(SessionStatus.Completed);
    },

    async logRejection(
      batchId: string,
      sessionId: string,
      reason: string
    ): Promise<void> {
      await this.createAuditLog({
        batchId,
        sessionId,
        action: AuditAction.Rejected,
        details: JSON.stringify({ reason }),
      });

      await this.updateSession(sessionId, { status: SessionStatus.InProgress });
    },

    async logSapCompletion(
      batchId: string,
      sessionId: string,
      usageDecisionDatetime: string,
      releaseStatus: 'released' | 'rejected'
    ): Promise<void> {
      await this.createAuditLog({
        batchId,
        sessionId,
        action: releaseStatus === 'released' ? AuditAction.Approved : AuditAction.Rejected,
        details: JSON.stringify({ source: 'SAP auto-sync', usageDecisionDatetime }),
      });
      await this.updateSession(sessionId, {
        status: releaseStatus === 'released' ? SessionStatus.Completed : SessionStatus.Rejected,
        completedAt: new Date(usageDecisionDatetime),
      });
    },
  };

  return client;
};

// Export a type alias for the session client (structurally === SessionAuditClient)
export type SessionAuditClient = ReturnType<typeof createSessionApi>;
